// ============================================================================
// PairsForm — Référentiels de compétences, UN par certification (lot 4)
// ============================================================================
// @systeme: certification-rs7137
// FX 2026-10-06 : « chaque certification doit disposer en base de son
// référentiel de compétences, avec ses indicateurs observables pour chaque
// compétence […] le rouge doit évidemment maîtriser le référentiel vert en
// plus du rouge (logique de progression) ».
//
// Deux documents, stockés dans `referentiels_codev` (org cecodev, pattern
// fork / retour à la version d'origine, éditables dans « Savoir métier
// Codev ») :
//   - `competences_vert`  — version d'origine = REFERENTIEL_VERT (RS 7137,
//     données `referentiel-rs7137.ts`) ; évalué par les voies RS7137 et
//     BADGE_VERT_DS ;
//   - `competences_rouge` — version d'origine = REFERENTIEL_ROUGE_DEFAUT
//     (squelette à compléter par Cecodev) ; évalué par l'ACCREDITATION. Le
//     document ne porte QUE les compétences propres au Module Rouge : la
//     résolution y AJOUTE les compétences vertes (`composerReferentielRouge`),
//     il n'y a donc qu'une seule source pour le vert.
//
// Ce fichier porte le MODÈLE (types), les versions d'origine, la validation
// d'un document édité à la main et les trois usages génériques : grille
// (compétences × critères), tableau du bilan, prose des prompts. Le serveur
// résout le référentiel d'une SESSION (`referentiel-competences.service.ts`)
// et le sert au client ; aucun écran ne lit plus la constante RS 7137.
// ============================================================================
import { COMPETENCES_RS7137, LIBELLES_VERDICT_RS7137, } from "./referentiel-rs7137.js";
const EPREUVES_COMPETENCE = ["SEANCE", "ENTRETIEN", "CAS_PRATIQUE", "DS"];
const LIBELLE_EPREUVE = {
    SEANCE: "la séance observée",
    ENTRETIEN: "l'entretien",
    CAS_PRATIQUE: "le cas pratique écrit",
    DS: "le débriefing de séance",
};
const LIBELLES_VERDICT = LIBELLES_VERDICT_RS7137;
// ----------------------------------------------------------------------------
// Versions d'origine
// ----------------------------------------------------------------------------
/** Référentiel vert = RS 7137, construit depuis la donnée source unique. */
export const REFERENTIEL_VERT = {
    parcours: "VERT",
    titre: "Référentiel RS 7137 — les 5 compétences et leurs critères observables",
    competences: COMPETENCES_RS7137.map((c) => ({
        code: c.code,
        nom: c.nom,
        nomCourt: c.nomCourt,
        definition: c.definition,
        epreuves: [...c.epreuves],
        criteres: c.criteres.map((k) => ({ ...k })),
    })),
    note: "La composition et l'organisation du groupe (nombre de participants, critères de sélection, fréquence des séances) relèvent de C5, pas de C1. C1 concerne uniquement le cadre sécurisant en séance.",
};
/**
 * Référentiel rouge — version d'origine : un SQUELETTE à compléter par
 * Cecodev dans « Savoir métier Codev » (FX fournit le référentiel). Les deux
 * entrées reprennent ce que l'application savait déjà du Module Rouge : le
 * repérage dans la problématique (bilan collectif) et le pilotage de projets
 * (titre de l'accréditation). Elles ne prétendent à aucune exactitude
 * officielle : intitulés, définitions et critères sont à rédiger.
 */
export const REFERENTIEL_ROUGE_DEFAUT = {
    parcours: "ROUGE",
    titre: "Référentiel de l'Accréditation du Cecodev — compétences du Module Rouge",
    introduction: "Version d'origine à compléter par Cecodev (Savoir métier Codev → Certifications). Les cinq compétences du référentiel vert (C1 à C5) s'ajoutent automatiquement à celles-ci : un accrédité maîtrise le vert ET le rouge.",
    competences: [
        {
            code: "R1",
            nom: "Repérer la problématique et accompagner son élaboration",
            nomCourt: "Repérage de la problématique",
            definition: "À rédiger par Cecodev : ce que l'Animateur accrédité fait, dans quel contexte et dans quel but.",
            epreuves: ["SEANCE", "ENTRETIEN", "DS"],
            criteres: [
                { code: "OR1_1", label: "Critère à définir", description: "À rédiger par Cecodev : ce que le jury observe." },
            ],
        },
        {
            code: "R2",
            nom: "Piloter un projet de Codéveloppement",
            nomCourt: "Pilotage de projet",
            definition: "À rédiger par Cecodev : ce que l'Animateur accrédité fait, dans quel contexte et dans quel but.",
            epreuves: ["ENTRETIEN", "DS"],
            criteres: [
                { code: "OR2_1", label: "Critère à définir", description: "À rédiger par Cecodev : ce que le jury observe." },
            ],
        },
    ],
};
/**
 * Le référentiel rouge ÉVALUÉ = compétences vertes + compétences rouges
 * (logique de progression, FX). Un code déjà présent dans le vert n'est pas
 * repris du rouge : le vert reste la seule source de C1-C5.
 */
export function composerReferentielRouge(vert, rouge) {
    const codesVert = new Set(vert.competences.map((c) => c.code));
    return {
        parcours: "ROUGE",
        titre: rouge.titre,
        introduction: rouge.introduction,
        competences: [
            ...vert.competences,
            ...rouge.competences.filter((c) => !codesVert.has(c.code)),
        ],
        note: [vert.note, rouge.note].filter((n) => !!n && n.trim().length > 0).join("\n") || undefined,
    };
}
// ----------------------------------------------------------------------------
// Accès
// ----------------------------------------------------------------------------
/** Compétences sous la forme `{ code, nom }` (init des verdicts, PV). */
export function competencesDuReferentiel(ref) {
    return ref.competences.map((c) => ({ code: c.code, nom: c.nom, nomCourt: c.nomCourt || c.nom }));
}
/** Critères par compétence, forme `{ code, label }` (grille jury, PV). */
export function criteresParCompetence(ref) {
    const acc = {};
    for (const c of ref.competences)
        acc[c.code] = c.criteres.map((k) => ({ code: k.code, label: k.label }));
    return acc;
}
// ----------------------------------------------------------------------------
// Prose des prompts
// ----------------------------------------------------------------------------
/**
 * Le référentiel en prose Markdown, injecté dans les prompts via
 * `{{referentiel}}`. Construit depuis les données : impossible qu'il diverge
 * de la grille.
 */
export function referentielMarkdown(ref) {
    const lignes = [];
    lignes.push(`## ${ref.titre}`);
    lignes.push("");
    if (ref.introduction?.trim()) {
        lignes.push(ref.introduction.trim());
        lignes.push("");
    }
    for (const c of ref.competences) {
        lignes.push(`### ${c.code} – ${c.nom}`);
        if (c.definition?.trim())
            lignes.push(c.definition.trim());
        if (c.criteres.length > 0) {
            lignes.push("Critères observables :");
            for (const k of c.criteres)
                lignes.push(`- **${k.label}** : ${k.description}`);
        }
        if (c.epreuves.length === 1) {
            lignes.push(`_${c.code} ne s'observe que dans ${LIBELLE_EPREUVE[c.epreuves[0]]}._`);
        }
        lignes.push("");
    }
    if (ref.note?.trim())
        lignes.push(`**Note** : ${ref.note.trim()}`);
    return lignes.join("\n").trimEnd();
}
/** Un verdict vide ou inconnu est INDÉTERMINÉ (lot O.6, délibération stricte : il ne vaut plus ACQUIS). */
function verdictEffectif(v) {
    if (v === "ACQUIS" || v === "NON_ACQUIS" || v === "ACQUIS_AVEC_RESERVE")
        return v;
    return "INDETERMINE";
}
/** Acquise = CHAQUE compétence du référentiel tranchée ACQUIS ou ACQUIS_AVEC_RESERVE (lot O.6). */
function certificationAcquiseDepuisVerdicts(ref, verdicts) {
    const parCode = new Map(verdicts.map((v) => [v.competenceCode, v]));
    return ref.competences.every((c) => {
        const v = verdictEffectif(parCode.get(c.code)?.verdictJury);
        return v === "ACQUIS" || v === "ACQUIS_AVEC_RESERVE";
    });
}
export function tableauCompetencesMarkdown(ref, verdicts, options = {}) {
    const avecCommentaires = options.avecCommentaires ?? true;
    const objet = options.objet ?? "certification";
    const parCode = new Map(verdicts.map((v) => [v.competenceCode, v]));
    const certifie = options.certifie ?? certificationAcquiseDepuisVerdicts(ref, verdicts);
    const lignes = [];
    lignes.push("| Compétence | Résultat |");
    lignes.push("|---|---|");
    for (const c of ref.competences) {
        const v = parCode.get(c.code);
        const verdict = verdictEffectif(v?.verdictJury);
        lignes.push(`| ${c.code} – ${c.nom} | ${LIBELLES_VERDICT[verdict]} |`);
    }
    lignes.push("");
    lignes.push(`**Résultat de la délibération** : ${objet} ${certifie ? "**acquise**" : "**non acquise**"}.`);
    const aDetailler = ref.competences.filter((c) => {
        const verdict = verdictEffectif(parCode.get(c.code)?.verdictJury);
        return verdict === "NON_ACQUIS" || verdict === "ACQUIS_AVEC_RESERVE";
    });
    if (aDetailler.length > 0) {
        lignes.push("");
        lignes.push("### Détail des critères");
        for (const c of aDetailler) {
            const v = parCode.get(c.code);
            const verdict = verdictEffectif(v.verdictJury);
            const nonAcquis = new Set(v.criteresNonAcquis ?? []);
            lignes.push("");
            lignes.push(`**${c.code} – ${c.nom}** : ${LIBELLES_VERDICT[verdict]}`);
            for (const k of c.criteres) {
                lignes.push(`- ${nonAcquis.has(k.code) ? "✗ Non validé" : "✓ Validé"} — ${k.label}`);
            }
            if (avecCommentaires && v.commentaireJury?.trim()) {
                lignes.push(`- _Observation du jury_ : ${v.commentaireJury.trim()}`);
            }
        }
    }
    return lignes.join("\n");
}
const RE_CODE_COMPETENCE = /^[A-Z]{1,3}\d{1,2}$/;
const RE_CODE_CRITERE = /^[A-Z0-9_]{2,16}$/;
function texte(v) {
    return typeof v === "string" ? v.trim() : "";
}
/**
 * Normalise et vérifie un référentiel (JSON saisi dans l'éditeur ou lu en
 * base). Les champs facultatifs reçoivent leur défaut ; les codes doivent
 * être uniques et sobres (ils sont stockés dans `verdicts_competences`).
 */
export function validerReferentielCompetences(contenu, parcoursAttendu) {
    const erreurs = [];
    const avertissements = [];
    if (contenu === null || typeof contenu !== "object" || Array.isArray(contenu)) {
        return { ok: false, erreurs: ["Le document doit être un objet JSON ({ titre, competences: [...] })."] };
    }
    const doc = contenu;
    const titre = texte(doc.titre);
    if (!titre)
        erreurs.push("`titre` requis.");
    const listeBrute = doc.competences;
    if (!Array.isArray(listeBrute) || listeBrute.length === 0) {
        erreurs.push("`competences` doit être une liste non vide.");
        return { ok: false, erreurs };
    }
    const codes = new Set();
    const codesCriteres = new Set();
    const competences = [];
    listeBrute.forEach((item, i) => {
        if (item === null || typeof item !== "object" || Array.isArray(item)) {
            erreurs.push(`competences[${i}] : objet attendu.`);
            return;
        }
        const c = item;
        const code = texte(c.code).toUpperCase();
        const nom = texte(c.nom);
        if (!RE_CODE_COMPETENCE.test(code))
            erreurs.push(`competences[${i}] : code « ${code || "?"} » invalide (lettres + chiffres, ex. C1, R2).`);
        else if (codes.has(code))
            erreurs.push(`competences[${i}] : code ${code} en double.`);
        codes.add(code);
        if (!nom)
            erreurs.push(`competences[${i}] (${code}) : \`nom\` requis.`);
        const epreuvesBrutes = Array.isArray(c.epreuves) ? c.epreuves : [];
        const epreuves = epreuvesBrutes
            .map((e) => texte(e).toUpperCase())
            .filter((e) => EPREUVES_COMPETENCE.includes(e));
        if (epreuvesBrutes.length > 0 && epreuves.length !== epreuvesBrutes.length) {
            avertissements.push(`${code} : épreuve(s) inconnue(s) ignorée(s) (valeurs admises : ${EPREUVES_COMPETENCE.join(", ")}).`);
        }
        const criteresBruts = Array.isArray(c.criteres) ? c.criteres : [];
        const criteres = [];
        criteresBruts.forEach((k, j) => {
            if (k === null || typeof k !== "object" || Array.isArray(k)) {
                erreurs.push(`${code} : criteres[${j}] objet attendu.`);
                return;
            }
            const kk = k;
            const kcode = texte(kk.code).toUpperCase();
            const label = texte(kk.label);
            if (!RE_CODE_CRITERE.test(kcode))
                erreurs.push(`${code} : criteres[${j}] code « ${kcode || "?"} » invalide (ex. OC1_1).`);
            else if (codesCriteres.has(kcode))
                erreurs.push(`${code} : critère ${kcode} en double.`);
            codesCriteres.add(kcode);
            if (!label)
                erreurs.push(`${code} : criteres[${j}] (${kcode}) \`label\` requis.`);
            criteres.push({ code: kcode, label, description: texte(kk.description) });
        });
        if (criteres.length === 0)
            avertissements.push(`${code} : aucun critère observable.`);
        competences.push({
            code,
            nom,
            nomCourt: texte(c.nomCourt) || nom,
            definition: texte(c.definition),
            epreuves: epreuves.length > 0 ? epreuves : [...EPREUVES_COMPETENCE],
            criteres,
        });
    });
    if (erreurs.length > 0)
        return { ok: false, erreurs };
    return {
        ok: true,
        avertissements,
        referentiel: {
            parcours: parcoursAttendu,
            titre,
            introduction: texte(doc.introduction) || undefined,
            competences,
            note: texte(doc.note) || undefined,
        },
    };
}
//# sourceMappingURL=referentiel-competences.js.map